// Shared mapping between a renter's stored profile columns, the in-app
// RenterPreferences shape, and the Browse filter values, so Profile, Browse
// and the admin simulator all read/write preferences the same way.
import { format, parseISO, startOfDay } from "date-fns";
import type { RenterPreferences } from "@/lib/matchScore";
import {
  BATHROOM_OPTIONS, BEDROOM_OPTIONS, TYPE_OPTIONS, areaKey, areaLabelFor, browseSearchToParams,
  type BrowseContext, type BrowseSearch,
} from "@/lib/browseFilters";

// Raw `profiles` row → RenterPreferences (the shape scoreProperty wants).
export const profileRowToPreferences = (row: Record<string, unknown> | null | undefined): RenterPreferences => {
  const r = (row ?? {}) as Record<string, unknown>;
  const date = (v: unknown) => (typeof v === "string" && v ? parseISO(v) : null);
  return {
    budgetMin: (r.preferred_budget_min as number) ?? null,
    budgetMax: (r.preferred_budget_max as number) ?? null,
    preferredAreas: (r.preferred_areas as string[]) ?? [],
    preferredTypes: (r.preferred_types as string[]) ?? [],
    preferredBedrooms: (r.preferred_bedrooms as number) ?? null,
    preferredBathrooms: (r.preferred_bathrooms as number) ?? null,
    preferredAmenities: (r.preferred_amenities as string[]) ?? [],
    preferredServices: (r.preferred_services as string[]) ?? [],
    moveInDate: date(r.move_in_date),
    moveOutDate: date(r.move_out_date),
    numPeople: (r.num_people as number) ?? null,
    hasPets: (r.has_pets as boolean) ?? null,
    dealbreakers: (r.preferred_dealbreakers as string[]) ?? [],
  };
};

// The subset of Browse filter state that maps to profile preferences.
export interface BrowseFilterValues {
  priceRange: [number, number];
  priceMin: number; // dynamic lower bound (handle here ⇒ "no min")
  priceMax: number; // dynamic upper bound (handle here ⇒ "no max")
  bedrooms: string; // "any" | "1".."5"
  bathrooms: string; // "any" | "1".."5"
  locations: string[];
  type: string; // "any" | a property type
  selectedAmenities: string[];
  selectedServices: string[];
  fromDate?: Date;
  toDate?: Date;
}

export interface ProfilePrefsPayload {
  preferred_budget_min: number | null;
  preferred_budget_max: number | null;
  preferred_areas: string[];
  preferred_area: string | null;
  preferred_types: string[];
  preferred_bedrooms: number | null;
  preferred_bathrooms: number | null;
  preferred_amenities: string[];
  preferred_services: string[];
  move_in_date: string | null;
  move_out_date: string | null;
}

// Browse filters → profiles update payload (preference columns only).
export const browseFiltersToProfilePayload = (f: BrowseFilterValues): ProfilePrefsPayload => ({
  // A handle resting on the dynamic bound means "unbounded" → store null.
  preferred_budget_min: f.priceRange[0] > f.priceMin ? f.priceRange[0] : null,
  preferred_budget_max: f.priceRange[1] < f.priceMax ? f.priceRange[1] : null,
  preferred_areas: f.locations,
  preferred_area: f.locations[0] ?? null,
  preferred_types: f.type !== "any" ? [f.type] : [],
  preferred_bedrooms: f.bedrooms !== "any" ? parseInt(f.bedrooms) : null,
  preferred_bathrooms: f.bathrooms !== "any" ? parseInt(f.bathrooms) : null,
  preferred_amenities: f.selectedAmenities,
  preferred_services: f.selectedServices,
  move_in_date: f.fromDate ? format(f.fromDate, "yyyy-MM-dd") : null,
  move_out_date: f.toDate ? format(f.toDate, "yyyy-MM-dd") : null,
});

// Drop deal-breaker tokens whose underlying preference is no longer present in
// the payload (e.g. an amenity was deselected). Keeps guests/pets tokens since
// Browse doesn't expose those filters. Never adds tokens.
export const pruneDealbreakers = (dealbreakers: string[], payload: ProfilePrefsPayload): string[] =>
  dealbreakers.filter((t) => {
    if (t.startsWith("area:")) return payload.preferred_areas.includes(t.slice(5));
    if (t.startsWith("type:")) return payload.preferred_types.includes(t.slice(5));
    if (t.startsWith("amenity:")) return payload.preferred_amenities.includes(t.slice(8));
    if (t.startsWith("service:")) return payload.preferred_services.includes(t.slice(8));
    if (t === "budget") return payload.preferred_budget_min != null || payload.preferred_budget_max != null;
    if (t === "bedrooms") return payload.preferred_bedrooms != null;
    if (t === "bathrooms") return payload.preferred_bathrooms != null;
    if (t === "dates") return payload.move_in_date != null;
    return true; // guests / pets, not Browse filters, leave untouched
  });

// --- The profile as a search, and back (Oct 13 check-in) -------------------
// A signed-in renter's search starts from their profile, and a search that
// no longer matches it offers to update the profile (and so their match).

const onlyIn = <T extends string>(options: readonly T[], value: string): T | undefined =>
  (options as readonly string[]).includes(value) ? (value as T) : undefined;

/**
 * The renter's profile as browse filters: only homes free for their dates,
 * within budget, with at least their bedrooms and bathrooms, in their areas,
 * with every deal-breaker amenity and service, and of their one type when
 * that type is a deal-breaker. A preferred type, amenity or service that is
 * not a deal-breaker shapes the match score but doesn't hide homes (Oct 15:
 * "apartment" alone emptied Santa Teresa under $2,000). Dates already over
 * are left out; a move-in in the past starts today.
 */
export function preferencesToBrowseSearch(prefs: RenterPreferences, ctx: BrowseContext, today: Date = new Date()): BrowseSearch {
  const t0 = startOfDay(today);
  const over = !!prefs.moveOutDate && prefs.moveOutDate < t0;
  const fromDate = prefs.moveInDate && !over ? (prefs.moveInDate < t0 ? t0 : prefs.moveInDate) : undefined;
  const toDate = fromDate && prefs.moveOutDate && prefs.moveOutDate > fromDate ? prefs.moveOutDate : undefined;
  const clamp = (n: number) => Math.min(ctx.priceMax, Math.max(ctx.priceMin, n));
  let lo = prefs.budgetMin ? clamp(prefs.budgetMin) : ctx.priceMin;
  let hi = prefs.budgetMax ? clamp(prefs.budgetMax) : ctx.priceMax;
  if (lo > hi) [lo, hi] = [ctx.priceMin, ctx.priceMax];
  const seen = new Set<string>();
  const locations = (prefs.preferredAreas ?? [])
    .map((a) => areaLabelFor(a, ctx))
    .filter((l): l is string => !!l && !seen.has(areaKey(l)) && !!seen.add(areaKey(l)));
  const breakers = prefs.dealbreakers ?? [];
  const pick = (prefix: string) => breakers.filter((t) => t.startsWith(prefix)).map((t) => t.slice(prefix.length)).filter(Boolean);
  return {
    locations,
    fromDate,
    toDate,
    priceRange: [lo, hi],
    bedrooms: (prefs.preferredBedrooms ? onlyIn(BEDROOM_OPTIONS, String(Math.min(5, prefs.preferredBedrooms))) : undefined) ?? "any",
    bathrooms: (prefs.preferredBathrooms ? onlyIn(BATHROOM_OPTIONS, String(Math.min(4, prefs.preferredBathrooms))) : undefined) ?? "any",
    type: (prefs.preferredTypes?.length === 1 && breakers.includes(`type:${prefs.preferredTypes[0]}`) ? onlyIn(TYPE_OPTIONS, prefs.preferredTypes[0]) : undefined) ?? "any",
    selectedAmenities: pick("amenity:"),
    selectedServices: pick("service:"),
    walkMax: undefined,
    stayMonths: undefined,
    sort: null,
  };
}

/**
 * Saving a search as the profile: "Any type" in a search the profile only
 * ranked by type keeps the profile's types (the search never showed them as a
 * filter, so saving must not wipe them).
 */
export function keepRankedTypes(payload: ProfilePrefsPayload, search: BrowseSearch, prefs: RenterPreferences | null, ctx: BrowseContext, today?: Date): ProfilePrefsPayload {
  if (search.type !== "any" || !prefs?.preferredTypes?.length) return payload;
  if (preferencesToBrowseSearch(prefs, ctx, today).type !== "any") return payload;
  return { ...payload, preferred_types: [...prefs.preferredTypes] };
}

/** The profile as URL params (browse keys only, no sort); empty when the profile sets nothing. */
export const preferencesToBrowseParams = (prefs: RenterPreferences, ctx: BrowseContext, today?: Date): URLSearchParams =>
  browseSearchToParams(preferencesToBrowseSearch(prefs, ctx, today), ctx);

/**
 * Whether a search says something the profile doesn't: other dates, budget,
 * rooms or areas; a type that is not one of the profile's; an amenity or
 * service that is not among the profile's preferences; or a deal-breaker left
 * out. Walk distance, stay length and the sort are not in the profile and
 * don't count.
 */
export function searchDiffersFromProfile(search: BrowseSearch, prefs: RenterPreferences, ctx: BrowseContext, today?: Date): boolean {
  const want = preferencesToBrowseSearch(prefs, ctx, today);
  // Narrowing to a preferred type the profile only ranks by says nothing new.
  const type = want.type === "any" && (prefs.preferredTypes ?? []).includes(search.type) ? "any" : search.type;
  const core = (x: BrowseSearch) => {
    const p = browseSearchToParams({ ...x, selectedAmenities: [], selectedServices: [], walkMax: undefined, stayMonths: undefined, sort: null }, ctx);
    const area = p.get("area");
    if (area) p.set("area", area.split(",").sort().join(","));
    return p.toString();
  };
  if (core({ ...search, type }) !== core(want)) return true;
  const extra = (have: string[], liked: string[], required: string[]) =>
    have.some((x) => !liked.includes(x) && !required.includes(x)) || required.some((x) => !have.includes(x));
  return extra(search.selectedAmenities, prefs.preferredAmenities ?? [], want.selectedAmenities)
    || extra(search.selectedServices, prefs.preferredServices ?? [], want.selectedServices);
}
